// src/components/ui/Dropdown.tsx
// Thin wrapper around Radix DropdownMenu — themed with CSS vars.
// Use this everywhere instead of importing Radix directly.

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as RadixDropdown from '@radix-ui/react-dropdown-menu';

type HoverCtx = {
  openNow: () => void;
  closeSoon: () => void;
  closeNow: () => void;
} | null;

const HoverDropdownContext = createContext<HoverCtx>(null);

/** Click-to-open root (default Radix behaviour). */
export const DropdownRoot = RadixDropdown.Root;
export const DropdownTrigger = RadixDropdown.Trigger;

/**
 * Hover-to-open root for desktop nav menus.
 * Keeps a short close delay so the pointer can move into the portaled panel.
 */
export function HoverDropdownRoot({
  children,
  openDelay = 40,
  closeDelay = 140,
}: {
  children: React.ReactNode;
  openDelay?: number;
  closeDelay?: number;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  // After a section click, ignore hover-open until the pointer leaves the trigger.
  const dismissed = useRef(false);

  const clearTimers = useCallback(() => {
    if (openTimer.current != null) window.clearTimeout(openTimer.current);
    if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  useEffect(() => {
    if (open || !dismissed.current) return;

    const unlock = (event: PointerEvent) => {
      const root = rootRef.current;
      const target = event.target;
      if (root && target instanceof Node && root.contains(target)) return;
      dismissed.current = false;
      document.removeEventListener('pointermove', unlock);
    };

    document.addEventListener('pointermove', unlock);
    return () => document.removeEventListener('pointermove', unlock);
  }, [open]);

  const openNow = useCallback(() => {
    if (dismissed.current) return;
    clearTimers();
    openTimer.current = window.setTimeout(() => setOpen(true), openDelay);
  }, [clearTimers, openDelay]);

  const closeSoon = useCallback(() => {
    clearTimers();
    closeTimer.current = window.setTimeout(() => setOpen(false), closeDelay);
  }, [clearTimers, closeDelay]);

  const closeNow = useCallback(() => {
    clearTimers();
    dismissed.current = true;
    setOpen(false);
  }, [clearTimers]);

  const onOpenChange = useCallback(
    (next: boolean) => {
      clearTimers();
      if (!next) dismissed.current = true;
      else dismissed.current = false;
      setOpen(next);
    },
    [clearTimers],
  );

  const releaseDismiss = useCallback(() => {
    dismissed.current = false;
  }, []);

  return (
    <HoverDropdownContext.Provider value={{ openNow, closeSoon, closeNow }}>
      <RadixDropdown.Root open={open} onOpenChange={onOpenChange} modal={false}>
        <span
          ref={rootRef}
          className="inline-flex"
          onMouseEnter={openNow}
          onMouseLeave={() => {
            releaseDismiss();
            closeSoon();
          }}
          onFocus={openNow}
        >
          {children}
        </span>
      </RadixDropdown.Root>
    </HoverDropdownContext.Provider>
  );
}

// ── Content (the floating panel) ──────────────────────────────────────────────
interface ContentProps {
  children: React.ReactNode;
  minWidth?: number;
  maxHeight?: number | string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  className?: string;
  /** Extra top padding / spacing for denser menus */
  padded?: boolean;
  /** Tight panel that hugs its items */
  compact?: boolean;
  /** Match the trigger button's width (Radix `--radix-dropdown-menu-trigger-width`). */
  matchTriggerWidth?: boolean;
  /** Keep false inside overflow-hidden dialogs so the menu is not squeezed shut. */
  avoidCollisions?: boolean;
  style?: React.CSSProperties;
}

export function DropdownContent({
  children,
  minWidth = 160,
  maxHeight,
  align = 'start',
  side = 'bottom',
  sideOffset = 4,
  className,
  padded = false,
  compact = false,
  matchTriggerWidth = false,
  avoidCollisions = true,
  style,
}: ContentProps) {
  const hover = useContext(HoverDropdownContext);
  const flush = Boolean(className && /(?:^|\s)!?p-0(?:\s|$)/.test(className));
  const transparent = Boolean(
    className && /(?:^|\s)!?bg-transparent(?:\s|$)/.test(className),
  );

  const maxHeightCss = typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;
  const resolvedMaxHeight = maxHeightCss
    ? avoidCollisions
      ? `min(${maxHeightCss}, var(--radix-dropdown-menu-content-available-height, ${maxHeightCss}))`
      : maxHeightCss
    : undefined;

  return (
    <RadixDropdown.Portal>
      <RadixDropdown.Content
        align={align}
        side={side}
        sideOffset={sideOffset}
        avoidCollisions={avoidCollisions}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onMouseEnter={() => hover?.openNow()}
        onMouseLeave={() => hover?.closeSoon()}
        className={className}
        collisionPadding={12}
        onWheel={(event) => event.stopPropagation()}
        style={{
          background: transparent ? 'transparent' : 'var(--bg-surface)',
          border: transparent || (className && className.includes('!border-0'))
            ? 'none'
            : '1px solid var(--border-default)',
          borderRadius: compact ? 'var(--radius-lg)' : 'var(--radius-xl)',
          boxShadow:
            transparent || (className && className.includes('!shadow-none'))
              ? 'none'
              : 'var(--shadow-lg)',
          minWidth: matchTriggerWidth
            ? 'var(--radix-dropdown-menu-trigger-width)'
            : minWidth,
          width: matchTriggerWidth
            ? 'var(--radix-dropdown-menu-trigger-width)'
            : 'max-content',
          maxWidth: matchTriggerWidth
            ? 'var(--radix-dropdown-menu-trigger-width)'
            : 'min(90vw, 320px)',
          padding: flush ? 0 : compact ? 4 : padded ? 8 : 6,
          // MUI Dialog/Modal layers are ~1300+, so keep dropdown above them.
          zIndex: 2000,
          pointerEvents: 'auto',
          outline: 'none',
          maxHeight: resolvedMaxHeight,
          overflowX: resolvedMaxHeight ? 'hidden' : undefined,
          overflowY: resolvedMaxHeight ? 'auto' : undefined,
          overscrollBehavior: resolvedMaxHeight ? 'contain' : undefined,
          scrollbarWidth: resolvedMaxHeight ? 'thin' : undefined,
          scrollbarColor: resolvedMaxHeight ? 'var(--border-default) transparent' : undefined,
          ...style,
        }}
      >
        {children}
      </RadixDropdown.Content>
    </RadixDropdown.Portal>
  );
}

// ── Item ───────────────────────────────────────────────────────────────────────
interface ItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  danger?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  compact?: boolean;
  wrap?: boolean;
  className?: string;
  /** Keep menu open after click (multi-select). Default true closes. */
  closeOnSelect?: boolean;
}

export function DropdownItem({
  children,
  onClick,
  danger = false,
  disabled = false,
  icon,
  compact = false,
  wrap = false,
  className,
  closeOnSelect = true,
}: ItemProps) {
  const hover = useContext(HoverDropdownContext);
  const bare = Boolean(className && className.includes('!p-0'));
  const customHover = Boolean(className && className.includes('hover:'));

  return (
    <RadixDropdown.Item
      onClick={onClick}
      disabled={disabled}
      className={['outline-none', className].filter(Boolean).join(' ')}
      onSelect={(event) => {
        if (!closeOnSelect) {
          event.preventDefault();
          return;
        }
        hover?.closeNow();
      }}
      style={{
        display: 'flex',
        alignItems: bare ? 'stretch' : wrap ? 'flex-start' : 'center',
        gap: bare ? 0 : compact ? 8 : 10,
        padding: bare ? 0 : compact ? '6px 8px' : '9px 12px',
        borderRadius: bare ? 0 : 'var(--radius-md)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        color: danger ? 'var(--text-danger)' : 'var(--text-primary)',
        fontSize: compact ? 12.5 : 13,
        fontWeight: 500,
        fontFamily: 'var(--font-sans)',
        outline: 'none',
        opacity: disabled ? 0.5 : 1,
        transition: 'background var(--transition-fast)',
        whiteSpace: wrap ? 'normal' : 'nowrap',
        background: 'transparent',
      }}
      onMouseEnter={(e) => {
        if (!disabled && !bare && !customHover) {
          (e.currentTarget as HTMLElement).style.background = danger
            ? 'color-mix(in srgb, var(--text-danger) 12%, transparent)'
            : 'var(--bg-subtle)';
        }
      }}
      onMouseLeave={(e) => {
        if (!customHover) {
          (e.currentTarget as HTMLElement).style.background = 'transparent';
        }
      }}
    >
      {icon ? (
        <span
          className={
            compact
              ? 'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md'
              : 'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-[var(--border-default)] bg-[var(--bg-subtle)]'
          }
          style={{ color: danger ? 'var(--text-danger)' : 'var(--text-secondary)' }}
        >
          {icon}
        </span>
      ) : null}
      <span className={bare ? 'flex w-full min-w-0' : 'min-w-0 flex-1'}>{children}</span>
    </RadixDropdown.Item>
  );
}

// ── Separator ─────────────────────────────────────────────────────────────────
export function DropdownSeparator({ compact = false }: { compact?: boolean }) {
  return (
    <RadixDropdown.Separator
      style={{
        height: 1,
        background: 'var(--border-subtle)',
        margin: compact ? '3px 2px' : '6px 4px',
      }}
    />
  );
}
